import React, { useState } from 'react';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from 'dompurify';
import { BrainCircuit, ChevronDown, Save, X } from 'lucide-react';
import { cn } from "@/lib/utils";
import MessageActions from "./MessageActions";
import ProposedChanges from "./ProposedChanges";

const MessageContent = ({ text = "", role, onAction, onEdit, id }) => {
  const [showThought, setShowThought] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(text || "");

  const safeText = typeof text === 'string' ? text : '';
  
  // Extract Thought
  const thoughtMatch = safeText.match(/<thought>([\s\S]*?)<\/thought>/);
  const partialThoughtMatch = safeText.match(/<thought>([\s\S]*?)$/); 
  const thought = thoughtMatch ? thoughtMatch[1].trim() : (partialThoughtMatch ? partialThoughtMatch[1].trim() : null);
  const isThoughtComplete = !!thoughtMatch;

  // Extract Commands
  const commandsMatch = safeText.match(/<commands>([\s\S]*?)<\/commands>/);
  let commands = [];
  try { if (commandsMatch) commands = JSON.parse(commandsMatch[1].trim()); } catch (e) {}

  // Clean Message Body
  let visibleMessage = safeText
    .replace(/<thought>[\s\S]*?<\/thought>/g, '')
    .replace(/<thought>[\s\S]*?$/g, '')
    .replace(/<commands>[\s\S]*?<\/commands>/g, '')
    .trim();

  const handleSaveEdit = () => { 
    onEdit(id, editText); 
    setIsEditing(false); 
  };

  if (role === 'user') {
    return (
      <div className="group flex flex-col items-end w-full">
        <div className="bg-sonas-surface2 rounded-2xl px-5 py-3.5 max-w-[80%] ml-auto text-[14.5px] leading-[1.7] border border-white/5 shadow-sm">
          {isEditing ? (
            <div className="space-y-3">
              <textarea 
                className="w-full bg-transparent border-none focus:ring-0 resize-none font-sans min-h-[80px] p-0 outline-none" 
                value={editText} 
                onChange={(e) => setEditText(e.target.value)} 
                autoFocus 
              />
              <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                <button onClick={() => setIsSignUp(false)} className="p-1.5 rounded-lg hover:bg-white/10 text-sonas-dim"><X size={14} /></button>
                <button onClick={handleSaveEdit} className="p-1.5 rounded-lg bg-sonas-accent text-sonas-bg"><Save size={14} /></button>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none prose-p:leading-relaxed prose-pre:bg-black/50">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{visibleMessage || "..."}</ReactMarkdown>
            </div>
          )}
        </div>
        {!isEditing && <MessageActions text={text} role={role} onAction={onAction} onEdit={() => setIsEditing(true)} />}
      </div>
    );
  }

  return (
    <div className="group w-full space-y-4">
      {(thought || partialThoughtMatch) && (
        <div className="w-full">
          <div className="flex items-center gap-2 mb-2">
            <button 
              onClick={() => setShowThought(!showThought)} 
              className="flex items-center gap-2 text-[10px] font-bold text-sonas-dim uppercase tracking-widest hover:text-sonas-accent transition-colors"
            >
              <BrainCircuit className={cn("h-3 w-3", showThought && "text-sonas-accent")} />
              Architect's Reasoning
              <ChevronDown className={cn("h-3 w-3 transition-transform duration-300", showThought && "rotate-180")} />
            </button>
            {!isThoughtComplete && <span className="h-1.5 w-1.5 rounded-full bg-sonas-accent animate-pulse" />}
          </div>
          {showThought && (
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 text-[12px] leading-relaxed text-sonas-muted italic animate-in slide-in-from-top-2 duration-300">
              <div className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{thought || "Mapping..."}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}
      
      {visibleMessage && (
        <div className="text-[14.5px] leading-[1.7]">
          <div className="prose prose-invert max-w-none prose-p:leading-relaxed prose-headings:font-syne prose-headings:mb-4 prose-pre:bg-black/40 prose-pre:border prose-pre:border-white/5">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {DOMPurify.sanitize(visibleMessage)}
            </ReactMarkdown>
          </div>
        </div>
      )}
      
      {commands.length > 0 && <ProposedChanges commands={commands} />}
      <MessageActions text={text} role={role} onAction={onAction} />
    </div>
  );
};

export default MessageContent;